import { FC } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useUserState } from "./common/UserContext";
import { UserPreferencesView } from "./components/user/UserPreferencesView";
import { MembersView } from "./components/members/MembersView";
import { WristbandLogin } from "./components/login/WristbandLogin";
import { isOnboarding, UserRole } from "./types/UserGroup";
import AppLayout, { NavKey, WithActiveNav } from "./components/layout/AppLayout";
import { Redirect } from "react-router6-redirect";
import ProtectedRoute from "./components/layout/ProtectedRoute";
import { VendorDetails } from "./components/vendor/VendorDetails";
import { SupplierDetailView } from "./components/supplier/detail/SupplierDetailView";
import { VendorsView } from "./components/vendor/list/VendorsView";
import { NegotiationsView } from "./components/negotiations/v2/list/NegotiationsView";
import { TacticView } from "./components/tactic/detail-v2/TacticView";
import { TacticListView } from "./components/tactic/list/TacticListView";
import { OrderFormDetailsView as OrderFormDetailsViewV2 } from "./components/orderform/detail-v2/OrderFormDetailsView";
import { ProjectListView } from "./components/project/ProjectListView";
import { ProjectView } from "./components/project/ProjectView";
import { DemoReportsListView, DemoReportDetailView } from "./components/reports/demo/DemoReports";
import { IhgBriefListView, IhgBriefDetailView } from "./components/ihg-demo/IhgBuyerBriefs";
import { Playground } from "./design-system/playground/Playground";
import {
    TailSpendLayout,
    TailSpendNegotiationsView,
    DealDetailView as TailSpendDealDetailView,
    TailSpendSuppliersView,
    TailSpendReportingView,
    GuardrailsView as TailSpendGuardrailsView,
    SetupView as TailSpendSetupView,
} from "./components/tailspend/demo";
import {
    BudgetDemoShell,
    BudgetOverviewView,
    BudgetTrackView,
    BudgetSourcesView,
} from "./components/budget/demo";
import { RenewalIntakeDemo } from "./components/intake/demo";
import { UsageIntakeView, UsageProjectView } from "./components/usage/demo";
import {
    DashboardApp,
    OrganizationsView,
    OrganizationDetailView,
    DeliveriesView,
} from "./components/superadmin/dashboard";
import { ImpersonateView } from "./components/superadmin/ImpersonateView";
import BuyerBriefListView from "./components/buyerbrief/BuyerBriefListView";
import BuyerBriefView from "./components/buyerbrief/BuyerBriefView";

export const AppRoutes: FC = () => {
    const { me } = useUserState();

    const indexElement = isOnboarding(me.roles ?? []) ? (
        <Navigate to="/project" />
    ) : (
        <NegotiationsView />
    );

    return (
        <>
            <Routes>
                {/* User pages */}
                <Route path="/" element={<AppLayout />}>
                    {/* These routes are always available */}
                    <Route element={<WithActiveNav navKey={NavKey.Negotiations} />}>
                        <Route index element={indexElement} />
                    </Route>
                    <Route path="login" element={<WristbandLogin />} />
                    <Route
                        path="preferences"
                        element={<WithActiveNav navKey={NavKey.Preferences} />}
                    >
                        <Route index element={<UserPreferencesView />} />
                        <Route path=":section" element={<UserPreferencesView />} />
                    </Route>
                    <Route path="project" element={<WithActiveNav navKey={NavKey.Projects} />}>
                        <Route index element={<ProjectListView />} />
                        <Route path=":projectId" element={<ProjectView />} />
                        <Route path=":projectId/intake" element={<ProjectView />} />
                        <Route path=":projectId/analysis/:version" element={<ProjectView />} />
                    </Route>

                    {/* These routes are only available if you are NOT onboarding */}
                    <Route
                        element={
                            <ProtectedRoute accessType={"DENY"} roles={[UserRole.ONBOARDING]} />
                        }
                    >
                        <Route path="dashboard" element={<Navigate to="/" />} />
                        <Route path="order" element={<WithActiveNav navKey={NavKey.Projects} />}>
                            <Route path=":orderFormId" element={<OrderFormDetailsViewV2 />} />
                        </Route>

                        <Route path="vendor" element={<WithActiveNav navKey={NavKey.Vendors} />}>
                            <Route index element={<VendorsView />} />
                            <Route path=":vendorKey" element={<VendorDetails />} />
                        </Route>

                        <Route
                            path="briefs"
                            element={<WithActiveNav navKey={NavKey.BuyerBriefs} />}
                        >
                            <Route index element={<BuyerBriefListView />} />
                            <Route path=":id" element={<BuyerBriefView />} />
                        </Route>

                        {/* Legacy briefs are view-only; the index lives at /briefs now. */}
                        <Route
                            path="supplier"
                            element={<WithActiveNav navKey={NavKey.BuyerBriefs} />}
                        >
                            <Route index element={<Redirect to="/briefs" />} />
                            <Route path=":id" element={<SupplierDetailView />} />
                        </Route>

                        {/* Buyer Briefs V2 moved out of /superadmin at GA. These keep
                            existing deep links working, and must stay outside the
                            super-admin gate or ProtectedRoute bounces non-admins to /
                            before the redirect can render. */}
                        <Route path="superadmin/brief">
                            <Route index element={<Redirect to="/briefs" />} />
                            <Route path=":id" element={<Redirect to="/briefs/:id" />} />
                        </Route>

                        <Route path="tactic" element={<WithActiveNav navKey={NavKey.Tactics} />}>
                            <Route index element={<TacticListView />} />
                            <Route path=":id" element={<TacticView />} />
                        </Route>

                        {/* These routes are only available to admins */}
                        <Route
                            element={
                                <ProtectedRoute accessType={"GRANT"} roles={[UserRole.ADMIN]} />
                            }
                        >
                            <Route path="admin">
                                <Route
                                    path="members"
                                    element={<WithActiveNav navKey={NavKey.Users} />}
                                >
                                    <Route index element={<MembersView />} />
                                </Route>
                                {/* The members page used to be the users page. */}
                                <Route path="users/*" element={<Redirect to="/admin/members" />} />
                            </Route>
                        </Route>
                    </Route>

                    {/* Any unknown path redirects to the index path */}
                    <Route path="*" element={<Navigate to="/" />} />
                </Route>

                {/* Superadmin pages */}
                <Route
                    path="superadmin"
                    element={<ProtectedRoute accessType={"GRANT"} roles={[UserRole.SUPER_ADMIN]} />}
                >
                    {/* Those routes use their own chrome, not AppLayout */}
                    <Route path="demo/intake" element={<RenewalIntakeDemo />} />
                    <Route path="demo/usage/project/:projectId/intake" element={<UsageIntakeView />} />
                    <Route path="demo/tail-spend" element={<TailSpendLayout />}>
                        <Route index element={<Navigate to="negotiations" replace />} />
                        <Route path="negotiations" element={<TailSpendNegotiationsView />} />
                        <Route path="negotiations/:dealId" element={<TailSpendDealDetailView />} />
                        <Route path="reporting" element={<TailSpendReportingView />} />
                        <Route path="suppliers" element={<TailSpendSuppliersView />} />
                        <Route path="suppliers/:supplierId" element={<TailSpendSuppliersView />} />
                        <Route path="guardrails" element={<TailSpendGuardrailsView />} />
                        <Route path="setup" element={<TailSpendSetupView />} />
                    </Route>
                    <Route path="dashboard" element={<DashboardApp />}>
                        <Route index element={<Navigate to="organizations" replace />} />
                        <Route path="organizations" element={<OrganizationsView />} />
                        <Route path="organizations/:orgId" element={<OrganizationDetailView />} />
                        <Route path="notifications" element={<DeliveriesView />} />
                    </Route>
                    {/* Pages under the oasis chrome. A second mount of
                        AppLayout: it remounts whenever navigation crosses in
                        or out of this branch. */}
                    <Route element={<AppLayout />}>
                        <Route
                            path="impersonate"
                            element={<WithActiveNav navKey={NavKey.SuperAdminMenu} />}
                        >
                            <Route index element={<ImpersonateView />} />
                        </Route>
                        <Route
                            path="demo/ihg-brief"
                            element={<WithActiveNav navKey={NavKey.BuyerBriefs} />}
                        >
                            <Route index element={<IhgBriefListView />} />
                            <Route path=":id" element={<IhgBriefDetailView />} />
                        </Route>
                        <Route
                            path="demo/reports"
                            element={<WithActiveNav navKey={NavKey.Reporting} />}
                        >
                            <Route index element={<DemoReportsListView />} />
                            <Route path=":id" element={<DemoReportDetailView />} />
                        </Route>
                        {/* The Usage demo's project page, reached from its Slack messages. */}
                        <Route
                            path="demo/usage/project/:projectId"
                            element={<WithActiveNav navKey={NavKey.Projects} />}
                        >
                            <Route index element={<UsageProjectView />} />
                        </Route>
                        <Route
                            path="demo/budget"
                            element={<WithActiveNav navKey={NavKey.Budget} />}
                        >
                            <Route element={<BudgetDemoShell />}>
                                <Route index element={<Navigate to="track" replace />} />
                                <Route path="track" element={<BudgetTrackView />} />
                                <Route
                                    path="forecast"
                                    element={<BudgetOverviewView mode="forecast" />}
                                />
                                <Route path="sources" element={<BudgetSourcesView />} />
                            </Route>
                        </Route>
                        <Route
                            path="playground"
                            element={<WithActiveNav navKey={NavKey.SuperAdminMenu} />}
                        >
                            <Route index element={<Playground />} />
                        </Route>
                    </Route>
                </Route>
            </Routes>
        </>
    );
};
